// src/App.tsx
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import {AuthBootstrap} from './context/AuthContext';
import {ProtectedRoute} from './components/ProtectedRoute';
import {AuthLayout} from './components/layout/AuthLayout.tsx';
import {Login} from './pages/Login';
import {AppraiserDashboard} from './pages/appraiser/dashboard/AppraiserDashboard.tsx';
import {NotFound} from "./pages/NotFound.tsx";
import {GlobalLoader} from "./context/LoadingContext.tsx";
import {NotificationViewport} from "./context/NotificationContext.tsx";
import {ApiBootstrap} from "./context/ApiContext.tsx";
import {UserType} from "./api/ApiTypes.ts";
import {AppraiserProfile} from "./pages/appraiser/profile/AppraiserProfile.tsx";
import {Unauthorized} from "./pages/UnAuthorized.tsx";
import UserTypeRouter from "./components/UserTypeRouter.tsx";
import {StaffDashboard} from "./pages/staff/StaffDashboard.tsx";
import {StaffOrderSearch} from "./pages/staff/StaffOrderSearch.tsx";
import {StaffProfile} from "./pages/staff/profile/StaffProfile.tsx";
import DualAuth from "./pages/DualAuth.tsx";
import {Payments} from "./pages/appraiser/payment/Payments.tsx";
import {OrderDetailsLayout} from "./pages/appraiser/orderdetails/OrderDetailsLayout.tsx";
import {SupportPage} from "./pages/appraiser/Support.tsx";
import {SecureLinks} from "./pages/appraiser/securelinks/SecureLinks.tsx";
import {QuoteDashboard} from "./pages/appraiser/quote/QuoteDashboard.tsx";
import {QuoteDetailsPage} from "./pages/appraiser/quote/QuoteDetailsPage.tsx";
import {ResetPassword} from "./pages/ResetPassword.tsx";
import {Forbidden} from "./pages/Forbidden.tsx";
import {LogOut} from "./pages/LogOut.tsx";
import {BackdoorLogin} from "./pages/secret/BackdoorLogin.tsx";
import {AppraisersPage} from "./pages/appraiserAdmin/AppraisersPage.tsx";
import {SecretDashboard} from "./pages/secret/SecretDashboard.tsx";
import {NoLongerAvailable} from "./pages/NoLongerAvailable.tsx";
import {ResetPasswordLanding} from "./pages/ResetPasswordLanding.tsx";
import {lazy, Suspense} from "react";
import {StaffOrderDetailsLayout} from "./pages/staff/orderdetails/StaffOrderDetailsLayout.tsx";
import {OrderDetailsRouter} from "./components/specialRouters/OrderDetailsRouter.tsx";
import {ReportFormPage} from "./pages/ReportFormPage.tsx";
import {SUBMIT_MODE} from "./components/dynamicforms/dyanimcFormUtil.tsx";

//sunspense and lazy allows to load the component only when needed (the text editor is heavy, so we want to load it only when needed)
const LazyViewFormDefinition = lazy(() => import('./pages/secret/ViewForm.tsx'));
const LazyCaCaptures = lazy(() => import('./pages/secret/CaCaptures.tsx'));
const LazyViewFormDefinitionV2 = lazy(() => import('./pages/secret/ViewFormDefinitionV2.tsx'));
const LazyManageProfiles = lazy(() => import('./pages/secret/ManageProfiles.tsx'));

export const App = () => {
    return (
        // Wrap the entire app in BrowserRouter to enable routing
        <BrowserRouter>
            {/*global way to indicate loading*/}
            {/*loader that listens to the loading status, and makes a loading section */}
            <GlobalLoader />
            <NotificationViewport/>
            {/*wraps entire app to allow use of api calls*/}
            <ApiBootstrap/>
            {/*wraps entire app to provide auth context*/}
            <AuthBootstrap />
            <Routes>
                {/* Root redirect - uses UserTypeRouter to determine where to go */}
                <Route index element={
                    <ProtectedRoute>
                        <UserTypeRouter basePath="dashboard" />
                    </ProtectedRoute>
                } />

                {/* Generic dashboard route */}
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <UserTypeRouter basePath="dashboard" />
                    </ProtectedRoute>
                } />

                {/* Generic profile route */}
                <Route path="/profile" element={
                    <ProtectedRoute>
                        <UserTypeRouter basePath="profile/me" />
                    </ProtectedRoute>
                } />

                {/* Generic order details route */}
                <Route
                    path="/orderdetails/:id"
                    element={
                        <ProtectedRoute>
                            <OrderDetailsRouter />
                        </ProtectedRoute>
                    }
                />


                <Route
                    path="/orderdetails/:id/:action"
                    element={
                        <ProtectedRoute>
                            <OrderDetailsRouter />
                        </ProtectedRoute>
                    }
                />

                {/* Appraiser Routes */}
                <Route path="/appraiser" element={
                    <ProtectedRoute restrictTo={[UserType.APPRAISER, UserType.APPRAISER_ADMIN, UserType.SECRET]}>
                        <AuthLayout />
                    </ProtectedRoute>
                }>

                    <Route path="profile" element={<Navigate to="/profile/me" replace />} />

                    <Route path="profile/:securityUserId"
                           element={<AppraiserProfile/>
                           }/>

                    <Route path="profile/:securityUserId/:tabName"
                           element={<AppraiserProfile/>
                           }/>
                    <Route path="quote/:id"
                           element={<QuoteDetailsPage/>}>
                    </Route>
                    <Route path="orderdetails/:id"
                           element={<OrderDetailsLayout/>}>
                    </Route>
                    <Route path="orderdetails/:id/:action"
                           element={<OrderDetailsLayout/>}>
                    </Route>
                    <Route path="secure-links/:secureLinkId"
                           element={<SecureLinks/>}/>

                    <Route path="secure-links" element={<SecureLinks/>}/>
                    <Route path="payments" element={<Payments />}/>
                    <Route path="payments/:tabName" element={<Payments />} />
                    <Route path="secure-links" element={<SecureLinks/>}/>
                    <Route path="dashboard" element={<AppraiserDashboard/>}/>
                    <Route path="support" element={<SupportPage/>}/>
                    <Route path="quotes" element={<QuoteDashboard/>}/>
                    <Route path="orderdetails/:id/no-longer-available" element={<NoLongerAvailable/>}/>
                    <Route path="orderdetails/:orderId/fill-form" element={<ReportFormPage mode={SUBMIT_MODE.APPRAISER_SUBMIT}/>}/>
                    <Route path="orderdetails/:orderId/view-form-read-only" element={<ReportFormPage mode={SUBMIT_MODE.READ_ONLY}/>}/>

                    {/* Add other appraiser-specific routes here */}
                </Route>

                {/* Appraiser-admin Routes */}
                <Route path="/appraiser-admin" element={
                    <ProtectedRoute restrictTo={[UserType.APPRAISER_ADMIN, UserType.SECRET]}>
                        <AuthLayout />
                    </ProtectedRoute>
                }>
                    <Route path="appraisers" element={<AppraisersPage />} />
                </Route>



                {/* Staff Routes */}
                <Route path="/staff" element={
                    <ProtectedRoute restrictTo={[UserType.STAFF, UserType.SECRET]}>
                        <AuthLayout />
                    </ProtectedRoute>
                }>
                    <Route path="dashboard" element={<StaffDashboard />} />
                    <Route path="order-search" element={<StaffOrderSearch />} />
                    <Route path="profile" element={<Navigate to="/profile/me" replace />} />

                    <Route path="profile/:securityUserId"
                           element={<StaffProfile/>
                           }/>
                    <Route path="orderdetails/:id"
                           element={<StaffOrderDetailsLayout/>}>
                    </Route>
                    <Route path="orderdetails/:id/:action"
                           element={<StaffOrderDetailsLayout/>}>
                    </Route>
                    <Route path="orderdetails/:orderId/qc"
                           element={<ReportFormPage mode={SUBMIT_MODE.QC}/>}>
                    </Route>
                    <Route path="orderdetails/:orderId/fill-form"
                           element={<ReportFormPage mode={SUBMIT_MODE.APPRAISER_SUBMIT}/>}/>
                    <Route path="orderdetails/:orderId/view-form-read-only" element={<ReportFormPage mode={SUBMIT_MODE.READ_ONLY}/>}/>
                    {/* Add other staff-specific routes here */}
                </Route>

                {/* IT Secret Route */}
                <Route path="/admin" element={
                    <ProtectedRoute restrictTo={[UserType.SECRET]}>
                        <AuthLayout />
                    </ProtectedRoute>
                }>
                    <Route path="dashboard" element={<SecretDashboard/>}></Route>
                    <Route path="backdoor-login" element={<BackdoorLogin/>} />
                    <Route path="forms" element={
                        //sunspense and lazy allows to load the component only when needed
                        <Suspense fallback={<div>Loading...</div>}>
                            <LazyViewFormDefinition />
                        </Suspense>
                    } />
                    <Route path="formsv2" element={
                        //sunspense and lazy allows to load the component only when needed
                        <Suspense fallback={<div>Loading...</div>}>
                            <LazyViewFormDefinitionV2 />
                        </Suspense>
                    } />
                    <Route path="ca-captures" element={
                        <Suspense fallback={<div>Loading...</div>}>
                            <LazyCaCaptures />
                        </Suspense>
                    }></Route>
                    <Route path="manage-profiles" element={
                        <Suspense fallback={<div>Loading...</div>}>
                            <LazyManageProfiles />
                        </Suspense>
                    }></Route>
                    <Route path="forms/:orderId" element={<ReportFormPage mode={SUBMIT_MODE.ADMIN}/>}/>
                </Route>

                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/unauthorized" element={<Unauthorized/>} />
                <Route path="/forbidden" element={<Forbidden/>} />
                <Route path="/logout" element={<LogOut/>} />

                <Route path="/dual-auth" element={<DualAuth />} />
                <Route path="/reset-password" element={<ResetPassword/>} />
                <Route path="/perform-reset-password" element={<ResetPasswordLanding/>}/>

                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </BrowserRouter>
    );
};

export default App;